import { useState, useEffect, useMemo } from 'react';
import TimeAgo from '../components/TimeAgo';
import {
  ArrowDownUp, RefreshCw, Loader2, AlertTriangle, Check, ChevronDown, X,
  Shield, Zap, Search, TrendingUp, TrendingDown, Clock, Target, Settings,
  Copy, ExternalLink, Brain, Sparkles, Eye, ArrowRight, BarChart2,
  Activity, Repeat, Timer, Info, Crosshair, Globe, Lock, Layers,
  Wallet, LogOut, CircleDot
} from 'lucide-react';
import { useAccount, useChainId, useSwitchChain, usePublicClient, useWalletClient, useBalance, useDisconnect } from 'wagmi';
import { parseUnits, encodeFunctionData, erc20Abi, formatUnits } from 'viem';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAuth } from '../hooks/useAuth';
import { InfoTip } from '../components/ui';
import {
  getSwapQuote, executeSwap, getSwapHistory, getTokenInfo,
  createLimitOrder, getLimitOrders, cancelLimitOrder, markLimitOrderFilled,
  createDcaSchedule, getDcaSchedules, cancelDcaSchedule,
  getTrendingTokens, analyzeMev, buildSwapTx,
} from '../services/api';

// Native-token sentinel — all DEX aggregators recognise this address as the
// native chain coin (ETH, MATIC, BNB, …). When it appears as the from-token
// the wallet sends `value` natively and no ERC-20 approval is needed.
const NATIVE_TOKEN_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

// Paraswap's TokenTransferProxy is the contract the user must approve so it
// can pull their ERC-20 before the swap executes. The address VARIES per chain
// — the legacy assumption that one address covered every chain was incorrect
// (Base in particular has a different address), and approving the Ethereum
// address on Base produced the MetaMask "deceptive request" Blockaid warning.
//
// We now ALWAYS source the spender from the backend's quote response (which
// extracts `tokenTransferProxy` directly from Paraswap's /prices payload — the
// only authoritative source). These per-chain known addresses exist only as a
// last-resort fallback if the backend fails to return one; they're checked
// against Paraswap's spender directory before use.
const KNOWN_PARASWAP_SPENDERS = {
  // Most v5 chains share the same TokenTransferProxy (deterministic deploy)…
  1:     '0x216B4B4Ba9F3e719726886d34a177484278Bfcae', // Ethereum
  137:   '0x216B4B4Ba9F3e719726886d34a177484278Bfcae', // Polygon
  56:    '0x216B4B4Ba9F3e719726886d34a177484278Bfcae', // BSC
  42161: '0x216B4B4Ba9F3e719726886d34a177484278Bfcae', // Arbitrum
  10:    '0x216B4B4Ba9F3e719726886d34a177484278Bfcae', // Optimism
  43114: '0x216B4B4Ba9F3e719726886d34a177484278Bfcae', // Avalanche
  // …but Base does NOT. Paraswap's Base v5 TokenTransferProxy is different.
  8453:  '0x93aAAe79a53759cD164340E4C8766E4Db5331cD7',
};

// Wagmi chain id → our string id used everywhere else in the app
const CHAIN_TO_WAGMI_ID = {
  ethereum: 1, polygon: 137, arbitrum: 42161, base: 8453,
  bsc: 56, avalanche: 43114, optimism: 10,
};

// ─── Numeric helpers ────────────────────────────────────────────────────────
// Quote responses sometimes return amounts as strings (raw smallest-unit
// BigInts from aggregator APIs). Calling .toFixed() on a string throws and
// unmounts the whole tree. These helpers coerce safely so the page can never
// blank out from a quote shape we didn't anticipate.
function toNum(v) {
  if (v == null) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
}
function fmtAmt(v, dp = 6) {
  const n = toNum(v);
  return n == null ? '—' : n.toFixed(dp);
}
// Falls back to a shortened contract address (0x1234…5678) when the symbol
// isn't known — previously we showed the literal word "UNKNOWN" all over the
// UI when a user pasted a custom token whose ERC-20 metadata we hadn't fetched.
function tokenLabel(t) {
  if (!t) return 'Select';
  if (t.symbol) return t.symbol;
  if (t.address && t.address.length >= 10) {
    return `${t.address.slice(0, 6)}…${t.address.slice(-4)}`;
  }
  return 'Token';
}

// ─── Constants ──────────────────────────────────────────────────────────────

const CHAINS = [
  { id: 'ethereum', name: 'Ethereum', icon: '⟠', color: 'text-blue-400' },
  { id: 'polygon',  name: 'Polygon',  icon: '🟣', color: 'text-violet-400' },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷', color: 'text-blue-300' },
  { id: 'base',     name: 'Base',     icon: '🔵', color: 'text-blue-400' },
  { id: 'bsc',      name: 'BSC',      icon: '🟡', color: 'text-yellow-400' },
  { id: 'avalanche',name: 'Avalanche',icon: '🔺', color: 'text-red-400' },
  { id: 'optimism', name: 'Optimism', icon: '🔴', color: 'text-red-400' },
];

// Popular tokens per chain. We deliberately do NOT include a `price` field —
// previous versions hardcoded stale prices (ETH at $3,450, WBTC at $67,500…)
// that showed in the USD-equivalent display and misled users. Live prices come
// from the real aggregator quote when the user requests one.
// Curated popular-tokens shortcut list per chain. NOT an exhaustive directory
// — the picker also accepts any 0x address (real-time GoPlus safety check),
// so anything off this list still works via paste. This is just the "tap to
// pick" quick row of the most-traded blue chips on each network.
const POPULAR_TOKENS = {
  ethereum: [
    { symbol: 'ETH',  address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WETH', address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', decimals: 18 },
    { symbol: 'USDC', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6  },
    { symbol: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6  },
    { symbol: 'DAI',  address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', decimals: 18 },
    { symbol: 'WBTC', address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', decimals: 8  },
    { symbol: 'LINK', address: '0x514910771AF9Ca656af840dff83E8264EcF986CA', decimals: 18 },
    { symbol: 'UNI',  address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', decimals: 18 },
    { symbol: 'AAVE', address: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9', decimals: 18 },
    { symbol: 'MKR',  address: '0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2', decimals: 18 },
    { symbol: 'ARB',  address: '0xB50721BCf8d664c30412Cfbc6cf7a15145234ad1', decimals: 18 },
    { symbol: 'PEPE', address: '0x6982508145454Ce325dDbE47a25d4ec3d2311933', decimals: 18 },
    { symbol: 'SHIB', address: '0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE', decimals: 18 },
    { symbol: 'MATIC', address: '0x7D1AfA7B718fb893dB30A3aBc0Cfc608AaCfeBB0', decimals: 18 },
    { symbol: 'RNDR', address: '0x6De037ef9aD2725EB40118Bb1702EBb27e4Aeb24', decimals: 18 },
  ],
  polygon: [
    { symbol: 'MATIC', address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WMATIC', address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', decimals: 18 },
    { symbol: 'USDC',  address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', decimals: 6  },
    { symbol: 'USDC.e', address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', decimals: 6  },
    { symbol: 'USDT',  address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6  },
    { symbol: 'DAI',   address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', decimals: 18 },
    { symbol: 'WETH',  address: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619', decimals: 18 },
    { symbol: 'WBTC',  address: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6', decimals: 8  },
    { symbol: 'LINK',  address: '0x53E0bca35eC356BD5ddDFebbD1Fc0fD03FaBad39', decimals: 18 },
    { symbol: 'AAVE',  address: '0xD6DF932A45C0f255f85145f286eA0b292B21C90B', decimals: 18 },
    { symbol: 'QUICK', address: '0xB5C064F955D8e7F38fE0460C556a72987494eE17', decimals: 18 },
  ],
  arbitrum: [
    { symbol: 'ETH',  address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WETH', address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1', decimals: 18 },
    { symbol: 'USDC', address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', decimals: 6  },
    { symbol: 'USDT', address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', decimals: 6  },
    { symbol: 'DAI',  address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1', decimals: 18 },
    { symbol: 'WBTC', address: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f', decimals: 8  },
    { symbol: 'ARB',  address: '0x912CE59144191C1204E64559FE8253a0e49E6548', decimals: 18 },
    { symbol: 'GMX',  address: '0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a', decimals: 18 },
    { symbol: 'LINK', address: '0xf97f4df75117a78c1A5a0DBb814Af92458539FB4', decimals: 18 },
    { symbol: 'MAGIC', address: '0x539bdE0d7Dbd336b79148AA742883198BBF60342', decimals: 18 },
    { symbol: 'GNS',  address: '0x18c11FD286C5EC11c3b683Caa813B77f5163A122', decimals: 18 },
    { symbol: 'RDNT', address: '0x3082CC23568eA640225c2467653dB90e9250AaA0', decimals: 18 },
  ],
  base: [
    { symbol: 'ETH',  address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WETH', address: '0x4200000000000000000000000000000000000006', decimals: 18 },
    { symbol: 'USDC', address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6  },
    { symbol: 'USDbC', address: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA', decimals: 6 },
    { symbol: 'DAI',  address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb', decimals: 18 },
    { symbol: 'cbETH', address: '0x2Ae3F1Ec7F1F5012CFEab0185bfc7aa3cf0DEc22', decimals: 18 },
    { symbol: 'wstETH', address: '0xc1CBa3fCea344f92D9239c08C0568f6F2F0ee452', decimals: 18 },
    { symbol: 'WBTC', address: '0x0555E30da8f98308EdB960aa94C0Db47230d2B9c', decimals: 8 },
    { symbol: 'AERO', address: '0x940181a94A35A4569E4529A3CDfB74e38FD98631', decimals: 18 },
    { symbol: 'DEGEN', address: '0x4ed4E862860beD51a9570b96d89aF5E1B0Efefed', decimals: 18 },
    { symbol: 'BRETT', address: '0x532f27101965dd16442E59d40670FaF5eBB142E4', decimals: 18 },
    { symbol: 'TOSHI', address: '0xAC1Bd2486aAf3B5C0fc3Fd868558b082a531B2B4', decimals: 18 },
    { symbol: 'MOG',  address: '0x2Da56AcB9Ea78330f947bD57C54119Debda7AF71', decimals: 18 },
    { symbol: 'HIGHER', address: '0x0578d8A44db98B23BF096A382e016e29a5Ce0ffe', decimals: 18 },
  ],
  bsc: [
    { symbol: 'BNB',  address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WBNB', address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', decimals: 18 },
    { symbol: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
    { symbol: 'USDC', address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
    { symbol: 'BUSD', address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', decimals: 18 },
    { symbol: 'ETH',  address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8', decimals: 18 },
    { symbol: 'BTCB', address: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c', decimals: 18 },
    { symbol: 'CAKE', address: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82', decimals: 18 },
    { symbol: 'DOGE', address: '0xbA2aE424d960c26247Dd6c32edC70B295c744C43', decimals: 8  },
    { symbol: 'SHIB', address: '0x2859e4544C4bB03966803b044A93563Bd2D0DD4D', decimals: 18 },
    { symbol: 'XRP',  address: '0x1D2F0da169ceB9fC7B3144628dB156f3F6c60dBE', decimals: 18 },
  ],
  avalanche: [
    { symbol: 'AVAX', address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WAVAX', address: '0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7', decimals: 18 },
    { symbol: 'USDC', address: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', decimals: 6  },
    { symbol: 'USDT', address: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7', decimals: 6  },
    { symbol: 'DAI.e', address: '0xd586E7F844cEa2F87f50152665BCbc2C279D8d70', decimals: 18 },
    { symbol: 'WETH.e', address: '0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB', decimals: 18 },
    { symbol: 'BTC.b', address: '0x152b9d0FdC40C096757F570A51E494bd4b943E50', decimals: 8 },
    { symbol: 'JOE',  address: '0x6e84a6216eA6dACC71eE8E6b0a5B7322EEbC0fDd', decimals: 18 },
    { symbol: 'LINK.e', address: '0x5947BB275c521040051D82396192181b413227A3', decimals: 18 },
  ],
  optimism: [
    { symbol: 'ETH',  address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', decimals: 18 },
    { symbol: 'WETH', address: '0x4200000000000000000000000000000000000006', decimals: 18 },
    { symbol: 'USDC', address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', decimals: 6  },
    { symbol: 'USDT', address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', decimals: 6  },
    { symbol: 'DAI',  address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1', decimals: 18 },
    { symbol: 'WBTC', address: '0x68f180fcCe6836688e9084f035309E29Bf0A2095', decimals: 8  },
    { symbol: 'OP',   address: '0x4200000000000000000000000000000000000042', decimals: 18 },
    { symbol: 'SNX',  address: '0x8700dAec35aF8Ff88c16BdF0418774CB3D7599B4', decimals: 18 },
    { symbol: 'VELO', address: '0x9560e827aF36c94D2Ac33a39bCE1Fe78631088Db', decimals: 18 },
    { symbol: 'sUSD', address: '0x8c6f28f2F1A3C87F0f938b96d27520d9751ec8d9', decimals: 18 },
  ],
};

const SLIPPAGE_PRESETS = [0.1, 0.5, 1.0, 3.0];

// ─── Pre-swap insights ──────────────────────────────────────────────────────
// Rule-based heuristics shown alongside the quote. Previously branded "AI Swap
// Advisor" — that overstated what it is. These are deterministic if/else rules,
// useful as pre-trade checks but not AI.
function getSwapInsights(fromSymbol, toSymbol, amount, chain) {
  const insights = [];
  const amountNum = parseFloat(amount) || 0;

  if (amountNum > 10000) insights.push({ type: 'warn', text: `Large swap ($${amountNum.toLocaleString()}) may cause significant price impact. Consider splitting into 2-3 smaller swaps.` });
  if (amountNum > 50000) insights.push({ type: 'critical', text: `Very large trade. Price impact could exceed 3%. Split into 5+ smaller orders over time using DCA.` });

  const hour = new Date().getUTCHours();
  if (hour >= 13 && hour <= 17) insights.push({ type: 'info', text: 'US market hours — typically highest DEX liquidity. Good time to execute large swaps.' });
  if (hour >= 1 && hour <= 6) insights.push({ type: 'info', text: 'Low liquidity hours (Asia/US overnight). Expect higher slippage on large orders.' });

  if (chain === 'ethereum' && amountNum < 100) insights.push({ type: 'warn', text: `Gas fees on Ethereum may be 5-15% of your swap value. Consider Base or Polygon for smaller trades.` });
  if (chain === 'base' || chain === 'arbitrum') insights.push({ type: 'good', text: `${chain === 'base' ? 'Base' : 'Arbitrum'} has sub-$0.10 gas fees. Cost-effective for any swap size.` });

  if (fromSymbol === 'USDC' || fromSymbol === 'USDT') insights.push({ type: 'good', text: `Buying ${toSymbol} with stablecoins — clean entry with no existing position risk.` });

  // Honest MEV note — manual swaps go through whichever RPC the user's wallet
  // is configured with (usually public mempool). We don't claim protection we
  // can't deliver. If the user wants real MEV protection, they need a Flashbots
  // RPC in their wallet.
  if (amountNum > 1000) {
    insights.push({ type: 'info', text: 'For large swaps, consider adding a private-mempool RPC (rpc.flashbots.net) to your wallet to reduce sandwich-attack risk. Public-mempool broadcasts are visible to MEV searchers.' });
  }

  const riskScore = amountNum > 50000 ? 8 : amountNum > 10000 ? 5 : amountNum > 1000 ? 3 : 1;
  return { insights, riskScore };
}

// ─── Token Picker Component ────────────────────────────────────────────────

function TokenPicker({ chain, selected, onSelect, onClose, label }) {
  const [search, setSearch] = useState('');
  const [customInfo, setCustomInfo] = useState(null);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const tokens = POPULAR_TOKENS[chain] || POPULAR_TOKENS.ethereum;

  // Treat the search box as dual-purpose: symbol/name OR contract address.
  // Pasting a 0x address auto-triggers the on-chain lookup so users don't
  // have to enter the address twice (in search + a separate field).
  const trimmed = search.trim();
  const isAddress = /^0x[a-fA-F0-9]{40}$/.test(trimmed);

  const filtered = tokens.filter(t =>
    !search || t.symbol.toLowerCase().includes(search.toLowerCase()) || t.address.toLowerCase().includes(search.toLowerCase())
  );

  // Auto-lookup whenever the search box contains a valid address. Cancellation
  // guard prevents stale results from a previous query overwriting a newer one
  // if the user types quickly.
  useEffect(() => {
    if (!isAddress) {
      setCustomInfo(null);
      setLookupError(null);
      return;
    }
    let cancelled = false;
    setLoadingCustom(true);
    setLookupError(null);
    setCustomInfo(null);
    getTokenInfo(chain, trimmed)
      .then(info => { if (!cancelled) setCustomInfo(info); })
      .catch(err => { if (!cancelled) setLookupError(err?.message || 'Lookup failed'); })
      .finally(() => { if (!cancelled) setLoadingCustom(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, chain, isAddress]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-sm max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-dark-700/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold">{label}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by symbol or paste a 0x… contract address"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:border-amber-500 focus:outline-none" autoFocus />
          {/* Inline status while resolving a pasted contract address */}
          {isAddress && (
            <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1.5">
              {loadingCustom ? (<><Loader2 className="w-3 h-3 animate-spin" /> Looking up token on-chain…</>) :
                lookupError ? (<><AlertTriangle className="w-3 h-3 text-red-400" /> <span className="text-red-400">{lookupError}</span></>) :
                customInfo ? (<><Check className="w-3 h-3 text-emerald-400" /> Token found — tap the card below to select.</>) :
                <>Detected contract address — fetching ERC-20 metadata…</>
              }
            </p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {/* Pasted-address result card — appears at the top so it's the first thing the user sees */}
          {isAddress && customInfo && (
            <button onClick={() => { onSelect({ symbol: customInfo.symbol || null, address: customInfo.address, decimals: customInfo.decimals ?? 18 }); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-400/30 bg-amber-400/5 hover:bg-amber-400/10 transition-colors mb-2">
              <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center text-xs font-bold text-amber-400">
                {(customInfo.symbol || '??').slice(0, 2)}
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium truncate">{customInfo.symbol || `${customInfo.address.slice(0, 6)}…${customInfo.address.slice(-4)}`}</span>
                  {customInfo.dataSource === 'goplus' && customInfo.rating ? (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      customInfo.rating === 'SAFE' ? 'bg-emerald-500/15 text-emerald-400' :
                      customInfo.rating === 'CAUTION' ? 'bg-amber-500/15 text-amber-400' :
                      'bg-red-500/15 text-red-400'
                    }`} title="Real on-chain data via GoPlus Security">{customInfo.rating} ({customInfo.safetyScore}/100)</span>
                  ) : (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-400" title={customInfo.reason || 'Safety data unavailable for this token'}>NO SAFETY DATA</span>
                  )}
                </div>
                <p className="text-gray-600 text-[10px] font-mono mt-0.5">{customInfo.address.slice(0, 10)}…{customInfo.address.slice(-8)}</p>
                {customInfo.dataSource === 'goplus' && (
                  <p className="text-gray-500 text-[10px] mt-1">
                    Holders: {customInfo.checks?.holderCount?.toLocaleString() || '—'}
                    {customInfo.checks?.liquidityUsd != null && ` · Liq: $${Math.round(customInfo.checks.liquidityUsd).toLocaleString()}`}
                  </p>
                )}
                {customInfo.dataSource !== 'goplus' && (
                  <p className="text-gray-600 text-[10px] mt-1">{customInfo.reason || 'Not in GoPlus database — proceed with caution'}</p>
                )}
              </div>
            </button>
          )}
          {/* Empty/error states for pasted-address mode */}
          {isAddress && !customInfo && !loadingCustom && lookupError && (
            <div className="px-4 py-6 text-center">
              <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-xs text-red-300">Couldn't fetch token info for this address.</p>
              <p className="text-[10px] text-gray-500 mt-1">Check that the address is correct for the {chain} chain.</p>
            </div>
          )}
          {/* Popular-list section label so users understand they're seeing the
              shortcut row, not the full universe of swappable tokens. */}
          {!isAddress && !search && filtered.length > 0 && (
            <p className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-wider text-gray-500">
              Popular on {chain}
            </p>
          )}
          {/* Popular token list — only shown when not in address-lookup mode */}
          {!isAddress && filtered.map(t => (
            <button key={t.address} onClick={() => { onSelect(t); onClose(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-dark-700 transition-colors ${selected?.address === t.address ? 'bg-amber-400/10' : ''}`}>
              <div className="w-8 h-8 rounded-full bg-dark-600 flex items-center justify-center text-xs font-bold text-white">
                {t.symbol.slice(0, 2)}
              </div>
              <div className="text-left flex-1">
                <p className="text-white text-sm font-medium">{t.symbol}</p>
                <p className="text-gray-600 text-[10px] font-mono">{t.address.slice(0, 6)}...{t.address.slice(-4)}</p>
              </div>
            </button>
          ))}
          {/* Footer hint — always visible when browsing the popular list, so
              users immediately know they can paste any contract address for
              tokens not in the curated list. Surfaces the "swap ANY token"
              affordance that's otherwise invisible. */}
          {!isAddress && (
            <div className="mx-2 mt-3 mb-2 px-3 py-2.5 rounded-xl border border-dashed border-dark-600 bg-dark-700/30">
              <p className="text-[11px] text-gray-400 leading-relaxed">
                <span className="text-amber-400 font-semibold">Don't see your token?</span> Paste any
                <span className="font-mono text-gray-300"> 0x… </span>contract address in the search box above —
                we'll fetch live metadata + a GoPlus safety score before you trade.
              </p>
            </div>
          )}
          {/* No-match state when search doesn't match popular tokens AND isn't an address */}
          {!isAddress && search && filtered.length === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-xs text-gray-400">No popular token matches "{search}"</p>
              <p className="text-[10px] text-gray-600 mt-1">Paste a full 0x… contract address to look up any ERC-20 token on this chain.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Route Visualizer ───────────────────────────────────────────────────────

function RouteVisualizer({ routes, bestIndex = 0 }) {
  if (!routes?.length) return null;
  return (
    <div className="space-y-2">
      {routes.map((r, i) => (
        <div key={i} className={`rounded-lg p-2.5 border ${i === 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="text-white text-xs font-medium">{r.aggregator}</span>
              {i === 0 && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">BEST</span>}
            </div>
            <span className={`text-xs font-mono font-bold ${i === 0 ? 'text-emerald-400' : 'text-gray-400'}`}>
              {fmtAmt(r.output, 6)}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {/* Protocol can arrive as a string (legacy/estimated path) OR an object
                {name, proportion} from live aggregator APIs. Rendering the raw
                object as a JSX child crashes React with "Objects are not valid
                as a React child", which previously unmounted the whole page. */}
            {(r.protocols || []).map((p, j) => {
              const label = typeof p === 'string' ? p : (p?.name || 'Unknown');
              return <span key={j} className="text-[9px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-500">{label}</span>;
            })}
          </div>
          <p className="text-[10px] text-gray-600 mt-1">Gas: ~${r.gasUsd}</p>
        </div>
      ))}
    </div>
  );
}

// ─── MEV Post-Trade Info ────────────────────────────────────────────────────
// Honest version. Previously claimed "Sandwich Attack Detected" or "Estimated
// savings: $X" based on fabricated values. Real post-hoc MEV analysis requires
// fetching the block, identifying frontrun/backrun txs, and computing actual
// price impact — we don't do that yet. We surface the protection tip instead
// of inventing a verdict.
function MevPostTradeInfo({ mevData }) {
  if (!mevData) return null;
  return (
    <div className="rounded-xl p-3 border bg-blue-500/5 border-blue-500/20">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-4 h-4 text-blue-400" />
        <span className="text-xs font-bold text-blue-400">MEV protection status</span>
      </div>
      {mevData.analysisAvailable === false ? (
        <>
          <p className="text-[10px] text-gray-400">{mevData.reason}</p>
          {mevData.protectionTip && (
            <p className="text-[10px] text-blue-300 mt-1">{mevData.protectionTip}</p>
          )}
        </>
      ) : (
        <p className="text-[10px] text-gray-400">Analysis pending. Refresh in a few minutes once the tx confirms.</p>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function SwapPage() {
  const { user } = useAuth();
  // Wagmi wallet state — required for the on-chain swap signing flow
  const { address: walletAddress, isConnected: isWalletConnected, connector: walletConnector } = useAccount();
  const walletChainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { disconnect } = useDisconnect();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  // Swap + History are the only tabs that survive — Limit Orders has its own
  // dedicated page (/dex-limit-orders) and Discover lives elsewhere. Keeping
  // those legacy paths intact for now in case any code path still queries them,
  // but they're no longer surfaced in the UI.
  const [activeTab, setActiveTab] = useState('swap'); // swap | history

  // Chain & token selection
  const [chain, setChain] = useState('ethereum');
  const [fromToken, setFromToken] = useState(null);
  const [toToken, setToToken] = useState(null);
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);
  const [amount, setAmount] = useState('');
  const [slippage, setSlippage] = useState(0.5);
  const [showSlippageSettings, setShowSlippageSettings] = useState(false);
  const [customSlippage, setCustomSlippage] = useState('');

  // Quote & execution
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [swapResult, setSwapResult] = useState(null);
  const [mevAnalysis, setMevAnalysis] = useState(null);

  // Limit orders
  const [limitPrice, setLimitPrice] = useState('');
  const [limitDirection, setLimitDirection] = useState('below');
  const [limitExpiry, setLimitExpiry] = useState(24);
  const [limitOrders, setLimitOrders] = useState([]);

  // DCA
  const [dcaAmount, setDcaAmount] = useState('');
  const [dcaFrequency, setDcaFrequency] = useState('daily');
  const [dcaTotalSwaps, setDcaTotalSwaps] = useState('30');
  const [dcaSchedules, setDcaSchedules] = useState([]);

  // History & discover
  const [swapHistory, setSwapHistory] = useState([]);
  const [trendingTokens, setTrendingTokens] = useState([]);
  const [trendingFetchedAt, setTrendingFetchedAt] = useState(null);
  const [quoteFetchedAt, setQuoteFetchedAt] = useState(null);
  const [quoteAgeSec, setQuoteAgeSec] = useState(0);

  // Live wallet balances for the selected From/To tokens. wagmi's useBalance
  // handles native vs ERC-20 transparently: omit `token` for the chain's native
  // coin, pass the contract address otherwise. We pin `chainId` to the SELECTED
  // page chain (not the wallet's current chain) so picking Base on the page
  // shows Base balances even while the wallet is still on Ethereum — the user
  // gets accurate context BEFORE switching networks.
  const expectedChainId = CHAIN_TO_WAGMI_ID[chain];
  const fromIsNative = fromToken?.address?.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase();
  const toIsNative   = toToken?.address?.toLowerCase()   === NATIVE_TOKEN_ADDRESS.toLowerCase();
  const { data: fromBalance } = useBalance({
    address: walletAddress,
    token: fromToken && !fromIsNative ? fromToken.address : undefined,
    chainId: expectedChainId,
    query: { enabled: !!walletAddress && !!fromToken && !!expectedChainId, refetchInterval: 15000 },
  });
  const { data: toBalance } = useBalance({
    address: walletAddress,
    token: toToken && !toIsNative ? toToken.address : undefined,
    chainId: expectedChainId,
    query: { enabled: !!walletAddress && !!toToken && !!expectedChainId, refetchInterval: 15000 },
  });

  // Native-coin balance — needed even when neither From nor To is the native
  // token, because gas is ALWAYS paid in the chain's native coin. If this is
  // zero, MetaMask silently switches to "Pay Gas with Token" relay mode, which
  // returns the opaque `FAILED_UNKNOWN` viem error and the user thinks the
  // platform is broken (see feedback_dex_gas_token_metamask.md). Warning before
  // Confirm Swap is how we prevent that rabbit hole for every future user.
  const { data: nativeBalance } = useBalance({
    address: walletAddress,
    chainId: expectedChainId,
    query: { enabled: !!walletAddress && !!expectedChainId, refetchInterval: 15000 },
  });

  // Symbol shown in the warning banner ("Get some ETH on Base", "Get some MATIC
  // on Polygon", etc.). Matches the native coin returned by wagmi.
  const NATIVE_SYMBOL_BY_CHAIN = {
    ethereum: 'ETH', arbitrum: 'ETH', base: 'ETH', optimism: 'ETH',
    polygon: 'MATIC', bsc: 'BNB', avalanche: 'AVAX',
  };
  const nativeSymbol = nativeBalance?.symbol || NATIVE_SYMBOL_BY_CHAIN[chain] || 'native';

  // "Functionally zero" — under 0.0001 native units. We don't trigger the
  // warning at exact 0 because rounding can land tiny dust there even when the
  // user has enough for gas. 0.0001 ETH on Base covers ~50 swaps; anything less
  // is a real shortage.
  const NATIVE_GAS_FLOOR = 0.0001;
  const nativeBalanceNum = nativeBalance?.value != null
    ? parseFloat(formatUnits(nativeBalance.value, nativeBalance.decimals))
    : null;
  const nativeBalanceLow = isWalletConnected
    && nativeBalanceNum != null
    && Number.isFinite(nativeBalanceNum)
    && nativeBalanceNum < NATIVE_GAS_FLOOR;

  // Format a wagmi `useBalance` result into a short human string.
  // Returns '—' when no value (loading / not connected).
  function fmtBalance(b) {
    if (!b || b.value == null) return '—';
    try {
      const human = formatUnits(b.value, b.decimals);
      const num = parseFloat(human);
      if (!Number.isFinite(num)) return '—';
      if (num === 0) return '0';
      if (num < 0.0001) return '<0.0001';
      if (num < 1)     return num.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
      if (num < 1000)  return num.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    } catch { return '—'; }
  }

  // Truncate a 0x address to "0x1234…abcd" for the wallet status bar.
  function shortAddr(a) {
    if (!a) return '';
    return `${a.slice(0, 6)}…${a.slice(-4)}`;
  }

  // Set the From amount to the wallet's full balance, minus a gas reserve for
  // native tokens (so the user has gas left to actually broadcast). Reserve
  // is conservative — 0.001 ETH covers a typical swap on any L2 + Mainnet at
  // moderate gas, and is dust-level on L1 mainnet.
  function handleMaxClick() {
    if (!fromBalance || fromBalance.value == null || !fromToken) return;
    try {
      const human = formatUnits(fromBalance.value, fromBalance.decimals);
      const num = parseFloat(human);
      if (!Number.isFinite(num) || num <= 0) return;
      let usable = num;
      if (fromIsNative) {
        const reserve = 0.001;
        usable = Math.max(0, num - reserve);
      }
      if (usable <= 0) return;
      setAmount(String(usable));
      setQuote(null);
    } catch {}
  }

  // Set defaults
  useEffect(() => {
    const tokens = POPULAR_TOKENS[chain] || POPULAR_TOKENS.ethereum;
    if (tokens.length >= 2) {
      setFromToken(tokens[0]);
      setToToken(tokens[1]);
    }
  }, [chain]);

  // Load data for tabs
  useEffect(() => {
    if (activeTab === 'history') loadHistory();
    if (activeTab === 'limit') loadLimitOrders();
    if (activeTab === 'dca') loadDcaSchedules();
    if (activeTab === 'discover') loadTrending();
  }, [activeTab, chain]);

  // Tick the quote age every second so the staleness UI updates live.
  useEffect(() => {
    if (!quoteFetchedAt) { setQuoteAgeSec(0); return undefined; }
    const id = setInterval(() => {
      setQuoteAgeSec((Date.now() - new Date(quoteFetchedAt).getTime()) / 1000);
    }, 1000);
    return () => clearInterval(id);
  }, [quoteFetchedAt]);

  async function loadHistory() {
    try { const d = await getSwapHistory(); setSwapHistory(d.swaps || []); } catch { setSwapHistory([]); }
  }
  async function loadLimitOrders() {
    try { const d = await getLimitOrders(); setLimitOrders(d.orders || []); } catch { setLimitOrders([]); }
  }
  async function loadDcaSchedules() {
    try { const d = await getDcaSchedules(); setDcaSchedules(d.schedules || []); } catch { setDcaSchedules([]); }
  }
  async function loadTrending() {
    try {
      const d = await getTrendingTokens(chain);
      setTrendingTokens(d.tokens || []);
      setTrendingFetchedAt(d.fetchedAt || new Date().toISOString());
    } catch {
      setTrendingTokens([]);
      setTrendingFetchedAt(null);
    }
  }

  async function handleGetQuote() {
    if (!fromToken || !toToken || !amount || parseFloat(amount) <= 0) return;
    setQuoting(true); setQuote(null); setQuoteFetchedAt(null); setQuoteAgeSec(0);
    try {
      const q = await getSwapQuote(chain, fromToken.address, toToken.address, amount, fromToken.decimals, toToken.decimals, slippage);
      setQuote(q);
      // Record when this quote was minted so we can show staleness.
      // Prefer the backend's timestamp if returned; otherwise use receipt time.
      setQuoteFetchedAt(q?.fetchedAt || q?.timestamp || new Date().toISOString());
    } catch (err) { console.error(err); }
    setQuoting(false);
  }

  /**
   * The real DEX swap execution flow:
   *  1. Verify wallet is connected and on the right chain
   *  2. Build the unsigned tx via backend (calls Paraswap /transactions)
   *  3. If from-token is ERC-20, check allowance for the Paraswap spender;
   *     if insufficient, send an approve() tx and wait for confirmation
   *  4. Send the swap tx itself — user signs in their wallet
   *  5. Wait for the on-chain receipt to confirm success
   *  6. Submit the resulting txHash to backend for tracking (pendingChecker
   *     will reconcile when the block is final)
   *
   * Step status is surfaced live in swapResult.step so the user knows where
   * we are in the flow.
   */
  async function handleExecuteSwap() {
    if (!fromToken || !toToken || !amount) return;
    if (quote?.source === 'estimated') {
      setSwapResult({ error: 'Cannot execute on an estimated quote. This pair/chain has no live aggregator route. Try a different chain or a more liquid pair.' });
      return;
    }
    if (!isWalletConnected) {
      setSwapResult({ error: 'Connect your wallet first.', needsConnect: true });
      return;
    }
    const wantedChainId = CHAIN_TO_WAGMI_ID[chain];
    if (wantedChainId && walletChainId !== wantedChainId) {
      try {
        setSwapResult({ step: 'switching-chain', error: null });
        await switchChainAsync({ chainId: wantedChainId });
      } catch (err) {
        setSwapResult({ error: `Switch your wallet to the ${chain} chain to continue.` });
        return;
      }
    }
    if (!walletClient || !publicClient) {
      setSwapResult({ error: 'Wallet not ready — try again in a moment.' });
      return;
    }

    // Reset all prior state cleanly so a stale error banner from a previous
    // attempt doesn't linger while a new attempt is in flight.
    setSwapResult(null);
    setMevAnalysis(null);
    setExecuting(true);

    try {
      const isNative = fromToken.address?.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase();
      const requiredAmount = !isNative ? parseUnits(String(amount), fromToken.decimals ?? 18) : 0n;

      // Resolve the correct Paraswap TokenTransferProxy DYNAMICALLY for the
      // user's chain. We get this from the backend's quote response (sourced
      // straight from Paraswap's /prices payload), never from a hardcoded
      // single-chain constant. This is what fixes the "deceptive request"
      // MetaMask warning on Base — Base's proxy address differs from mainnet.
      let resolvedSpender = null;
      let prebuiltTx = null;

      // (1) Build the swap tx. If allowance is insufficient, the backend
      // returns { needsApproval: true, spender } so we can approve the right
      // address. Otherwise it returns the full unsigned tx.
      setSwapResult({ step: 'building', error: null });
      const buildResp = await buildSwapTx({
        chain,
        fromToken: fromToken.address,
        toToken: toToken.address,
        amount,
        slippage,
        userAddress: walletAddress,
        fromDecimals: fromToken.decimals,
        toDecimals: toToken.decimals,
      });

      if (buildResp?.spender) {
        resolvedSpender = buildResp.spender;
      } else if (!isNative) {
        // Backend didn't return one — fall back to the known per-chain map.
        // This should rarely fire in practice; if it does, it means the
        // backend or Paraswap is broken in some new way and the user's chain
        // isn't in our known list either, so we abort instead of guessing.
        resolvedSpender = KNOWN_PARASWAP_SPENDERS[CHAIN_TO_WAGMI_ID[chain]] || null;
        if (!resolvedSpender) {
          throw new Error(`Couldn't resolve the Paraswap spender for ${chain}. Refresh the quote and try again.`);
        }
      }

      if (buildResp?.success === false && !buildResp.needsApproval) {
        throw new Error(buildResp.message || 'Could not build the swap transaction.');
      }
      if (buildResp?.success !== false) {
        prebuiltTx = buildResp;
      }

      // (2) ERC-20 approval — only if from-token is an ERC-20. Approves the
      // exact swap amount × 1.05 (5% buffer for any slippage / fee adjustment
      // Paraswap makes between quote and execution). NEVER unlimited — that
      // pattern is what triggered the original Blockaid security warning even
      // when the spender was correct.
      async function readAllowance(spender) {
        return publicClient.readContract({
          address: fromToken.address,
          abi: erc20Abi,
          functionName: 'allowance',
          args: [walletAddress, spender],
        });
      }

      if (!isNative) {
        setSwapResult({ step: 'checking-allowance', error: null });
        let allowance = await readAllowance(resolvedSpender);

        if (allowance < requiredAmount) {
          // USDT (Ethereum: 0xdAC17F958D2ee523a2206206994597C13D831ec7) and a few
          // other non-standard ERC-20s revert when approve(spender, X) is called
          // while current allowance is non-zero. The fix is to first set it to 0,
          // then to the target. Harmless for compliant tokens, mandatory for USDT.
          if (allowance > 0n) {
            setSwapResult({
              step: 'approving',
              error: null,
              note: `${tokenLabel(fromToken)} requires resetting its old allowance to zero before raising it. Please approve the 0-reset in MetaMask (Signature 1/3).`,
            });
            const zeroHash = await walletClient.writeContract({
              address: fromToken.address,
              abi: erc20Abi,
              functionName: 'approve',
              args: [resolvedSpender, 0n],
            });
            setSwapResult({
              step: 'waiting-approval',
              error: null,
              approveHash: zeroHash,
              note: 'Resetting allowance to zero on-chain… (~15-30s). A second approval popup will appear right after.',
            });
            const RESET_POLL_MS = 4000;
            const RESET_MAX_WAIT_MS = 180_000;
            const resetStart = Date.now();
            while (Date.now() - resetStart < RESET_MAX_WAIT_MS) {
              await new Promise(r => setTimeout(r, RESET_POLL_MS));
              try { allowance = await readAllowance(resolvedSpender); } catch {}
              if (allowance === 0n) break;
            }
            if (allowance !== 0n) {
              throw new Error(
                `${tokenLabel(fromToken)} allowance reset didn't confirm in 3 minutes. Check your wallet's Activity tab — if it shows Confirmed there, click Confirm Swap again.`
              );
            }
          }

          // Approve EXACT amount + 5% buffer. Not unlimited.
          // Buffer reasoning: Paraswap may rebuild the tx with slightly
          // different srcAmount due to gas-token routing tweaks, and we want
          // the approval to still cover the final amount. 5% is conservative
          // enough that token risk is bounded ("at worst they can take 1.05x
          // what I'm swapping right now") but generous enough to not block.
          const approvalAmount = (requiredAmount * 105n) / 100n;
          setSwapResult({
            step: 'approving',
            error: null,
            note: `📝 First swap of ${tokenLabel(fromToken)} on ${chain} = 2 signatures total. The first signature in MetaMask approves Paraswap to spend the EXACT amount you're swapping (no unlimited approval). The second is the swap itself.`,
          });
          const approveHash = await walletClient.writeContract({
            address: fromToken.address,
            abi: erc20Abi,
            functionName: 'approve',
            args: [resolvedSpender, approvalAmount],
          });
          setSwapResult({
            step: 'waiting-approval',
            error: null,
            approveHash,
            note: '✓ Approval signed. Polling on-chain (~15-30s)… The swap signature will appear right after.',
          });

          // Poll allowance directly. Far more reliable than waitForTransactionReceipt
          // because we don't depend on any particular tx hash — only the
          // on-chain state being what we need.
          const POLL_MS = 4000;
          const MAX_WAIT_MS = 180_000; // 3 min — generous for slow networks
          const start = Date.now();
          while (Date.now() - start < MAX_WAIT_MS) {
            await new Promise(r => setTimeout(r, POLL_MS));
            try { allowance = await readAllowance(resolvedSpender); } catch { /* RPC hiccup */ }
            if (allowance >= requiredAmount) break;
            const elapsed = Math.floor((Date.now() - start) / 1000);
            setSwapResult({
              step: 'waiting-approval',
              error: null,
              approveHash,
              note: `Waiting for approval to land on-chain… (${elapsed}s elapsed). If MetaMask shows the approval as Confirmed in your Activity tab, this will catch up shortly.`,
            });
          }
          if (allowance < requiredAmount) {
            throw new Error(
              `Approval didn't confirm within 3 minutes. Check your wallet's Activity tab — if the approval shows Confirmed there, click Confirm Swap again (it will skip approval). If it shows Failed or Dropped, try once more.`
            );
          }
        }
      }

      // (3) If we didn't get a tx in step 1 (because allowance was missing),
      // rebuild now that approval is in place. We also rebuild whenever the
      // original tx is older than ~30s to avoid stale priceRoute.
      let tx = prebuiltTx;
      const needsRebuild = !tx || (tx?.timestamp && Date.now() - new Date(tx.timestamp).getTime() > 30000);
      if (needsRebuild) {
        setSwapResult({ step: 'building', error: null });
        tx = await buildSwapTx({
          chain,
          fromToken: fromToken.address,
          toToken: toToken.address,
          amount,
          slippage,
          userAddress: walletAddress,
          fromDecimals: fromToken.decimals,
          toDecimals: toToken.decimals,
        });
        if (tx?.success === false) {
          throw new Error(tx.message || 'Could not build the swap transaction after approval. Try again.');
        }
      }

      // (3) Send the swap transaction
      setSwapResult({ step: 'signing-swap', error: null });
      const swapHash = await walletClient.sendTransaction({
        to: tx.to,
        data: tx.data,
        value: tx.value && tx.value !== '0x0' ? BigInt(tx.value) : undefined,
        gas: tx.gasLimit ? BigInt(tx.gasLimit) : undefined,
      });

      // (4) Wait for receipt
      setSwapResult({ step: 'waiting-receipt', error: null, txHash: swapHash });
      const receipt = await publicClient.waitForTransactionReceipt({ hash: swapHash });

      // (5) Submit to backend for tracking so the swap shows up in History
      // and the pending-checker reconciles the on-chain state. Best-effort —
      // a tracking failure doesn't undo the successful on-chain swap, but we
      // surface a small note so the user knows.
      let trackingNote = null;
      try {
        // The quote's bestRoute.output is the human-readable expected receive
        // amount (e.g. 0.001020 for 2 USDC → WETH). We pass it so cost-basis
        // ledger has a non-zero amountOut on the buy event — otherwise the
        // ledger drops the buy entirely (toQty=0 fails the > 0 guard).
        const expectedAmountOut = toNum(quote?.bestRoute?.output) ?? null;
        await executeSwap(
          chain,
          fromToken.address,
          toToken.address,
          fromToken.symbol || '?',
          toToken.symbol || '?',
          amount,
          slippage,
          tx.aggregator,
          quote?.fetchedAt || quote?.quotedAt,
          swapHash,
          expectedAmountOut,
        );
      } catch (err) {
        console.warn('Swap tracking submission failed (swap itself succeeded):', err);
        trackingNote = 'Tracking record could not be saved — your swap completed on-chain, but it may not appear in History. Refresh later if missing.';
      }

      setSwapResult({
        step: 'done',
        error: null,
        success: receipt.status === 'success',
        txHash: swapHash,
        blockNumber: Number(receipt.blockNumber),
        trackingNote,
        swap: {
          amountIn: amount,
          fromSymbol: fromToken.symbol,
          toSymbol: toToken.symbol,
          txHash: swapHash,
        },
      });
    } catch (err) {
      console.error('[Swap] execution error:', err);
      // Distinguish "user cancelled in wallet" (not a bug) from real failures.
      // viem throws specific error names for these so we can show a calmer
      // message instead of scary "Internal Server Error" text.
      const isUserReject =
        err?.name === 'UserRejectedRequestError' ||
        err?.code === 4001 ||
        /user rejected|user denied|user cancelled|user canceled/i.test(err?.message || '') ||
        /user rejected|user denied|user cancelled|user canceled/i.test(err?.shortMessage || '');

      if (isUserReject) {
        setSwapResult({
          userCanceled: true,
          note: 'You cancelled the wallet signature — no transaction was sent. Click Confirm Swap to try again.',
        });
        return;
      }

      // Real error: surface as much as we can so root cause is visible.
      const parts = [];
      if (err?.shortMessage) parts.push(err.shortMessage);
      else if (err?.message) parts.push(err.message);
      if (err?.details && err.details !== err?.shortMessage) parts.push(err.details);
      if (err?.cause?.message && err.cause.message !== err?.message) parts.push(`(${err.cause.message})`);
      const msg = parts.join(' — ') || 'Swap failed';
      setSwapResult({ error: msg });
    } finally {
      setExecuting(false);
    }
  }

  function handleFlipTokens() {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    setQuote(null);
  }

  async function handleCreateLimitOrder() {
    if (!fromToken || !toToken || !amount || !limitPrice) return;
    if (!walletAddress) { alert('Connect your wallet first.'); return; }
    try {
      await createLimitOrder(
        chain, fromToken.address, toToken.address,
        fromToken.symbol, toToken.symbol,
        parseFloat(amount), parseFloat(limitPrice),
        limitDirection, parseInt(limitExpiry) || 168,
        walletAddress,
        fromToken.decimals ?? 18, toToken.decimals ?? 18,
      );
      await loadLimitOrders();
      alert('Limit order placed. The worker will notify you the moment your target price is hit.');
    } catch (err) { alert(err.message); }
  }

  // Execute a triggered limit order at current market: build tx via Paraswap,
  // wallet signs, broadcast, then tell backend the txHash.
  async function handleExecuteLimitOrder(order) {
    if (!walletClient || !publicClient) { alert('Connect your wallet first.'); return; }
    try {
      // Build the swap tx (reuses the same buildSwapTx path our DEX Swap uses)
      const tx = await buildSwapTx({
        chain: order.chain,
        fromToken: order.fromToken,
        toToken: order.toToken,
        amount: order.fromAmount,
        slippage: 1.0, // 1% on a triggered order; market may have moved
        userAddress: walletAddress,
        fromDecimals: order.fromDecimals,
        toDecimals: order.toDecimals,
      });
      // Send transaction
      const hash = await walletClient.sendTransaction({
        to: tx.to, data: tx.data, value: tx.value ? BigInt(tx.value) : 0n,
      });
      await publicClient.waitForTransactionReceipt({ hash });
      // Tell backend
      await markLimitOrderFilled(order.orderId, {
        txHash: hash,
        filledPrice: order.lastSeenPrice || order.targetPrice,
        filledAmount: order.fromAmount,
      });
      await loadLimitOrders();
      alert('Limit order filled.');
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Execute failed';
      if (/user rejected|user denied/i.test(msg)) return; // silent on cancel
      alert(msg);
    }
  }

  async function handleCreateDca() {
    if (!fromToken || !toToken || !dcaAmount || !dcaTotalSwaps) return;
    try {
      await createDcaSchedule(chain, fromToken.address, toToken.address, fromToken.symbol, toToken.symbol, dcaAmount, dcaFrequency, dcaTotalSwaps, slippage);
      await loadDcaSchedules();
    } catch (err) { alert(err.message); }
  }

  const swapInsights = useMemo(() => fromToken && toToken ? getSwapInsights(fromToken.symbol, toToken.symbol, amount, chain) : null, [fromToken, toToken, amount, chain]);
  const effectiveSlippage = customSlippage ? parseFloat(customSlippage) : slippage;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center"><ArrowDownUp className="w-5 h-5 text-white" /></div>
            DEX Swap
          </h1>
          <p className="text-gray-500 text-sm mt-1 flex items-center gap-1">
            Trade any token across 7 chains — 3-aggregator routing (0x · Paraswap · 1inch), real GoPlus token safety, rule-based pre-trade checks
            <InfoTip side="bottom">First-time DEX user? Start on Base (sub-cent gas). Ethereum mainnet has full liquidity but $10-40 per swap in gas — only use it for trades over a few hundred dollars.</InfoTip>
          </p>
        </div>
        {/* Chain selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {CHAINS.map(c => (
            <button key={c.id} onClick={() => setChain(c.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                chain === c.id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'
              }`}>
              <span>{c.icon}</span> {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* ── Wallet Status Bar ── */}
      {/* Surfaces the connected wallet identity + chain mismatch warnings.
          Without this, "Balance: —" on the swap card was confusing for users
          coming from Uniswap/1inch (which always show wallet state up top).
          When disconnected, shows the RainbowKit connect button so users have
          a single place to start. */}
      <div className="bg-gradient-to-r from-dark-800 to-dark-800/60 rounded-xl border border-dark-700/50 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {isWalletConnected ? (
            <>
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <Wallet className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono text-sm font-bold">{shortAddr(walletAddress)}</span>
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(walletAddress); } catch {} }}
                    className="p-1 rounded hover:bg-dark-700 text-gray-500 hover:text-amber-400"
                    title="Copy address"
                  ><Copy className="w-3 h-3" /></button>
                </div>
                <div className="flex items-center gap-2 text-[11px] mt-0.5">
                  <CircleDot className={`w-2.5 h-2.5 ${walletChainId === expectedChainId ? 'text-emerald-400' : 'text-amber-400'}`} />
                  {walletChainId === expectedChainId ? (
                    <span className="text-gray-500">Connected on {CHAINS.find(c => c.id === chain)?.name || chain}{walletConnector?.name ? ` · via ${walletConnector.name}` : ''}</span>
                  ) : (
                    <span className="text-amber-400">
                      Wallet is on chain {walletChainId} — switch to {CHAINS.find(c => c.id === chain)?.name || chain} to swap
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-9 h-9 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                <Wallet className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">No wallet connected</div>
                <div className="text-gray-500 text-[11px]">Connect to see balances + execute swaps</div>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isWalletConnected ? (
            <>
              {walletChainId !== expectedChainId && (
                <button
                  onClick={() => { switchChainAsync?.({ chainId: expectedChainId }).catch(() => {}); }}
                  className="px-3 py-1.5 rounded-lg bg-amber-400/15 border border-amber-400/40 text-amber-400 text-xs font-bold hover:bg-amber-400/25"
                >
                  Switch network
                </button>
              )}
              <button
                onClick={() => disconnect()}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-dark-700 border border-dark-600 text-gray-400 hover:text-red-400 text-xs font-medium"
                title="Disconnect wallet"
              >
                <LogOut className="w-3.5 h-3.5" /> Disconnect
              </button>
            </>
          ) : (
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <button
                  onClick={openConnectModal}
                  className="px-4 py-2 rounded-lg bg-amber-400 text-dark-900 text-xs font-bold hover:bg-amber-300"
                >
                  Connect Wallet
                </button>
              )}
            </ConnectButton.Custom>
          )}
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      {/* Limit Order, DCA, and Discover tabs were removed 2026-06-01 — they all
          have dedicated top-level pages now (DEX Limit Orders, DEX DCA, Discover
          menu). Keeping the underlying state + load functions wired so any
          remaining direct callers don't break. */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: 'swap', label: 'Swap', icon: ArrowDownUp },
          { id: 'history', label: 'History', icon: Clock },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'
            }`}>
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Centered single-column layout — matches the industry standard for DEX
          UIs (Uniswap, 1inch, Matcha). Pre-swap checks and Route Comparison now
          stack below the swap card naturally instead of needing a side column. */}
      <div className={`mx-auto space-y-4 ${activeTab === 'swap' ? 'max-w-xl' : 'max-w-5xl'}`}>

        <>

          {activeTab === 'swap' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold flex items-center gap-2"><ArrowDownUp className="w-4 h-4 text-blue-400" /> Swap</h3>
                <div className="flex items-center gap-1">
                  <InfoTip side="left">Slippage = max % the price can move before the swap reverts. 0.5% is fine for blue chips; 1-3% needed for memecoins. Too low = swap fails; too high = sandwich risk.</InfoTip>
                  <button onClick={() => setShowSlippageSettings(!showSlippageSettings)} className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-dark-700"><Settings className="w-4 h-4" /></button>
                </div>
              </div>

              {/* Slippage settings */}
              {showSlippageSettings && (
                <div className="bg-dark-700/50 rounded-xl p-3 mb-4 border border-dark-600/30">
                  <p className="text-gray-500 text-[10px] mb-2">Slippage Tolerance</p>
                  <div className="flex gap-1.5">
                    {SLIPPAGE_PRESETS.map(s => (
                      <button key={s} onClick={() => { setSlippage(s); setCustomSlippage(''); }}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold ${slippage === s && !customSlippage ? 'bg-amber-400 text-dark-900' : 'bg-dark-600 text-gray-400'}`}>{s}%</button>
                    ))}
                    <input type="number" value={customSlippage} onChange={e => setCustomSlippage(e.target.value)} placeholder="Custom" step="0.1" min="0.01" max="50"
                      className="w-20 bg-dark-600 border border-dark-500 rounded-lg px-2 py-1.5 text-white text-xs font-mono text-center placeholder-gray-600 focus:outline-none" />
                  </div>
                  {effectiveSlippage > 5 && <p className="text-red-400 text-[10px] mt-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> High slippage — you may receive significantly fewer tokens</p>}
                </div>
              )}

              {/* From token */}
              <div className="bg-dark-700/30 rounded-xl p-4 border border-dark-600/30 mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-xs">You pay</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-[10px]">
                      Balance: <span className="text-gray-300 font-mono">{fmtBalance(fromBalance)}</span>
                      {fromToken && <span className="text-gray-600"> {tokenLabel(fromToken)}</span>}
                    </span>
                    {isWalletConnected && fromBalance?.value > 0n && (
                      <button
                        onClick={handleMaxClick}
                        className="px-2 py-0.5 rounded bg-amber-400/15 border border-amber-400/30 text-amber-400 text-[10px] font-bold hover:bg-amber-400/25"
                        title={fromIsNative ? 'Use full balance minus a small gas reserve' : 'Use full balance'}
                      >MAX</button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input type="number" value={amount} onChange={e => { setAmount(e.target.value); setQuote(null); }}
                    placeholder="0.0" step="any" min="0"
                    className="flex-1 bg-transparent text-white text-2xl font-mono font-bold placeholder-gray-700 focus:outline-none" />
                  <button onClick={() => setShowFromPicker(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-600 hover:bg-dark-500 border border-dark-500 transition-all">
                    <span className={fromToken ? "text-white text-sm font-bold" : "text-gray-400 text-sm"}>{tokenLabel(fromToken)}</span><ChevronDown className="w-3 h-3 text-gray-500" />
                  </button>
                </div>
                {/* USD-equivalent removed — was based on stale hardcoded prices. Live USD comes from the quote response below once the user requests one. */}
              </div>

              {/* Flip button */}
              <div className="flex justify-center -my-1 relative z-10">
                <button onClick={handleFlipTokens}
                  className="w-10 h-10 rounded-xl bg-dark-700 border border-dark-600 flex items-center justify-center text-gray-400 hover:text-amber-400 hover:border-amber-400/30 transition-all hover:rotate-180 duration-300">
                  <ArrowDownUp className="w-4 h-4" />
                </button>
              </div>

              {/* To token */}
              <div className="bg-dark-700/30 rounded-xl p-4 border border-dark-600/30 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500 text-xs">You receive</span>
                    <span className="text-gray-500 text-[10px]">
                      Balance: <span className="text-gray-300 font-mono">{fmtBalance(toBalance)}</span>
                      {toToken && <span className="text-gray-600"> {tokenLabel(toToken)}</span>}
                    </span>
                  </div>
                  {quote?.priceImpact && (
                    <span className={`text-[10px] flex items-center gap-1 ${parseFloat(quote.priceImpact) > 2 ? 'text-red-400' : 'text-gray-600'}`}>
                      Impact: {quote.priceImpact}%
                      <InfoTip side="left">How much YOUR swap moves the market price. Under 0.3% is healthy. Over 2% means thin liquidity — break the order into smaller pieces via DEX Algo Orders.</InfoTip>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <p className="flex-1 text-2xl font-mono font-bold text-white">
                    {quoting ? <Loader2 className="w-6 h-6 animate-spin text-gray-500" /> : (toNum(quote?.bestRoute?.output) != null ? fmtAmt(quote.bestRoute.output, 6) : '0.0')}
                  </p>
                  <button onClick={() => setShowToPicker(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-600 hover:bg-dark-500 border border-dark-500 transition-all">
                    <span className={toToken ? "text-white text-sm font-bold" : "text-gray-400 text-sm"}>{tokenLabel(toToken)}</span><ChevronDown className="w-3 h-3 text-gray-500" />
                  </button>
                </div>
                {quote?.minimumReceived != null && <p className="text-gray-600 text-xs mt-1">Min received: {fmtAmt(quote.minimumReceived, 6)} {tokenLabel(toToken)} (at {effectiveSlippage}% slippage)</p>}
              </div>

              {/* Quote details */}
              {quote && (
                <div className="bg-dark-700/30 rounded-xl p-3 mt-3 space-y-1.5 border border-dark-600/30">
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Rate</span><span className="text-white font-mono">1 {tokenLabel(fromToken)} = {fmtAmt(quote.executionPrice, 6)} {tokenLabel(toToken)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Route</span><span className="text-amber-400">{quote.bestRoute?.aggregator}</span></div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Gas estimate</span>
                    {quote.gasEstimateUsd != null ? (
                      <span className="text-white">~${fmtAmt(quote.gasEstimateUsd, 2)}</span>
                    ) : quote.gasUnits != null ? (
                      <span className="text-gray-400">~{quote.gasUnits.toLocaleString()} gas units · wallet shows USD</span>
                    ) : (
                      <span className="text-gray-500">Check wallet for gas cost</span>
                    )}
                  </div>
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Savings vs worst</span><span className="text-emerald-400">+{quote.savingsBps ?? 0} bps</span></div>
                  {/* MEV protection line removed — manual swaps go through the user's wallet RPC, which is typically public mempool. Real protection requires a Flashbots-style RPC. Pre-swap checks include guidance for large swaps. */}
                  {quoteFetchedAt && (
                    <div className="flex justify-between text-xs pt-1 border-t border-dark-600/30 mt-1">
                      <span className="text-gray-500">Quote freshness</span>
                      <TimeAgo timestamp={quoteFetchedAt} prefix="from" />
                    </div>
                  )}
                </div>
              )}

              {/* Quote staleness warning — surfaces between quote fetch and execute */}
              {quote && quoteAgeSec > 30 && quoteAgeSec <= 60 && quote?.source !== 'estimated' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 mt-3 flex items-start gap-2 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-300 flex-1">
                    This quote is {Math.floor(quoteAgeSec)}s old. Prices may have moved.
                    <button onClick={handleGetQuote} className="underline hover:text-amber-200 ml-1">Refresh quote</button>
                  </span>
                </div>
              )}
              {quote && quoteAgeSec > 60 && quote?.source !== 'estimated' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 mt-3 flex items-start gap-2 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-red-300 flex-1">
                    <strong>Quote expired.</strong> Refresh before executing.
                  </span>
                </div>
              )}

              {/* Native-gas-balance warning — fires when wallet has effectively
                  zero {nativeSymbol}. MetaMask silently switches to "pay gas
                  with token" relay mode in that state, which returns the opaque
                  FAILED_UNKNOWN error from viem. Warning here gives the user
                  the actionable fix BEFORE they sign anything. */}
              {nativeBalanceLow && (
                <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 mt-3 flex items-start gap-2 text-xs">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-red-300 font-bold mb-1">
                      You have no {nativeSymbol} on {CHAINS.find(c => c.id === chain)?.name || chain} for gas.
                    </p>
                    <p className="text-red-200/80 leading-relaxed">
                      Gas on every chain is paid in its native coin ({nativeSymbol} here, not the token you're swapping). Without it, your wallet will reject the swap with a vague "Transaction relay error" — even though the swap itself is valid.
                      <br />
                      <span className="text-red-200">
                        Fix: bridge or buy a small amount of {nativeSymbol} on {CHAINS.find(c => c.id === chain)?.name || chain} (~0.001 covers many swaps on L2s), then retry.
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="space-y-2 mt-4">
                {!quote ? (
                  <button onClick={handleGetQuote} disabled={quoting || !fromToken || !toToken || !amount || parseFloat(amount) <= 0}
                    className="w-full py-3.5 rounded-xl text-sm font-bold bg-gradient-to-r from-blue-500 to-violet-500 text-white hover:from-blue-400 hover:to-violet-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
                    {quoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Get Best Quote
                  </button>
                ) : (
                  <>
                    {quote?.source === 'estimated' && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 flex items-start gap-2 text-xs">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-amber-300">
                          <strong>Estimated quote (not live).</strong> No aggregator route available for this pair/chain. Cannot execute — pick a more liquid pair or different chain.
                        </span>
                      </div>
                    )}
                    {!isWalletConnected ? (
                      <div className="space-y-2">
                        <p className="text-[11px] text-gray-400 text-center">Connect your wallet to sign and broadcast the swap.</p>
                        <ConnectButton.Custom>
                          {({ openConnectModal }) => (
                            <button onClick={openConnectModal}
                              className="w-full py-3.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 transition-all flex items-center justify-center gap-2">
                              <Zap className="w-4 h-4" /> Connect Wallet
                            </button>
                          )}
                        </ConnectButton.Custom>
                      </div>
                    ) : swapResult?.step === 'done' && swapResult?.success ? (
                      // Successful swap: replace Confirm Swap with a clear
                      // "Start new swap" reset button. Critical UX guard —
                      // previously the Confirm Swap button stayed clickable
                      // after success, so an over-eager user could accidentally
                      // trigger a second identical swap. Now they must
                      // explicitly start a new one.
                      <button
                        onClick={() => {
                          setSwapResult(null);
                          setQuote(null);
                          setQuoteFetchedAt(null);
                          setQuoteAgeSec(0);
                          setAmount('');
                          setMevAnalysis(null);
                        }}
                        className="w-full py-3.5 rounded-xl text-sm font-bold bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-400 hover:to-blue-500 transition-all flex items-center justify-center gap-2"
                      >
                        <Zap className="w-4 h-4" /> Start new swap
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          // If the quote has expired, refresh it in place instead of
                          // forcing a two-click "cancel → refresh → confirm" dance.
                          if (quoteAgeSec > 60) handleGetQuote();
                          else handleExecuteSwap();
                        }}
                        disabled={executing || quote?.source === 'estimated' || quoting || nativeBalanceLow}
                        className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-40 ${
                          quoteAgeSec > 60
                            ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500'
                            : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-400 hover:to-emerald-500'
                        }`}>
                        {executing || quoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        {quote?.source === 'estimated'
                          ? 'Cannot execute estimated quote'
                          : nativeBalanceLow
                            ? `Need ${nativeSymbol} for gas`
                            : quoting
                              ? 'Refreshing quote…'
                              : quoteAgeSec > 60
                                ? 'Refresh quote'
                                : executing && swapResult?.step
                                ? ({
                                    'building': 'Preparing transaction…',
                                    'switching-chain': 'Switching wallet network…',
                                    'checking-allowance': 'Checking token approval…',
                                    'approving': 'Sign #1/2 in wallet (Approval)…',
                                    'waiting-approval': 'Waiting for approval to confirm…',
                                    'signing-swap': 'Sign #2/2 in wallet (Swap)…',
                                    'waiting-receipt': 'Waiting for on-chain confirmation…',
                                  }[swapResult.step] || 'Confirming…')
                                : 'Confirm Swap'}
                      </button>
                    )}
                    <button onClick={() => { setQuote(null); setQuoteFetchedAt(null); setQuoteAgeSec(0); setSwapResult(null); }}
                      className="w-full py-2.5 rounded-xl text-xs text-gray-500 hover:text-white transition-colors">
                      Cancel & get new quote
                    </button>
                  </>
                )}
              </div>

              {/* Swap result — shows error, in-progress note, or final confirmation.
                  In-progress steps (signing, waiting) are surfaced on the
                  Confirm button itself; this panel only renders when we have
                  a real error or a final successful receipt. */}
              {swapResult?.error && (
                <div className="rounded-xl p-4 mt-3 border bg-red-500/5 border-red-500/20">
                  <p className="text-xs text-red-400">{swapResult.error}</p>
                </div>
              )}
              {swapResult?.userCanceled && (
                <div className="rounded-xl p-3 mt-3 border bg-blue-500/5 border-blue-500/20 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-300">{swapResult.note}</p>
                </div>
              )}
              {swapResult?.note && !swapResult.error && (
                <div className="rounded-xl p-3 mt-3 border bg-blue-500/5 border-blue-500/20 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />
                  <p className="text-xs text-blue-300">{swapResult.note}</p>
                </div>
              )}
              {swapResult?.step === 'done' && !swapResult.error && (
                <div className={`rounded-xl p-4 mt-3 border ${swapResult.success ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                  <div className="text-center">
                    {swapResult.success ? (
                      <>
                        <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-emerald-400 font-bold">Swap confirmed on-chain</p>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                        <p className="text-amber-400 font-bold">Transaction reverted</p>
                        <p className="text-[11px] text-amber-300/80 mt-1">The wallet broadcast succeeded but the tx reverted on-chain. Check the explorer for revert reason.</p>
                      </>
                    )}
                    <p className="text-gray-400 text-xs mt-1">{swapResult.swap?.amountIn} {tokenLabel(fromToken)} → {tokenLabel(toToken)}</p>
                    {swapResult.txHash && (
                      <a href={`https://etherscan.io/tx/${swapResult.txHash}`} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-gray-500 mt-2 font-mono inline-flex items-center gap-1 hover:text-amber-400">
                        {swapResult.txHash.slice(0, 12)}…{swapResult.txHash.slice(-10)} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {swapResult.trackingNote && (
                      <p className="text-[10px] text-amber-300/80 mt-2 italic">{swapResult.trackingNote}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Post-swap MEV info (honest — no fabricated "sandwiched / saved $X" verdict) */}
              {mevAnalysis && <div className="mt-3"><MevPostTradeInfo mevData={mevAnalysis} /></div>}
            </div>
          )}

          {/* ── Limit Order Tab ── */}
          {activeTab === 'limit' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Target className="w-4 h-4 text-amber-400" /> DEX Limit Order</h3>
              <p className="text-gray-500 text-xs mb-4">Set a target price. We monitor the market every 30s and notify you the moment your target is hit — then one click executes the swap at current market.</p>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button onClick={() => setShowFromPicker(true)} className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-sm text-white text-left">{fromToken?.symbol || 'From'} <ChevronDown className="w-3 h-3 inline ml-1 text-gray-500" /></button>
                  <button onClick={() => setShowToPicker(true)} className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-sm text-white text-left">{toToken?.symbol || 'To'} <ChevronDown className="w-3 h-3 inline ml-1 text-gray-500" /></button>
                </div>
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount" className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
                <input type="number" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} placeholder="Target price (USD)" className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setLimitDirection('below')} className={`py-2 rounded-lg text-xs font-medium ${limitDirection === 'below' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-dark-700 text-gray-500 border border-dark-600'}`}>Buy when below</button>
                  <button onClick={() => setLimitDirection('above')} className={`py-2 rounded-lg text-xs font-medium ${limitDirection === 'above' ? 'bg-red-500/15 text-red-400 border border-red-500/30' : 'bg-dark-700 text-gray-500 border border-dark-600'}`}>Sell when above</button>
                </div>
                <select value={limitExpiry} onChange={e => setLimitExpiry(parseInt(e.target.value))} className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none">
                  <option value={1}>Expires in 1 hour</option>
                  <option value={6}>Expires in 6 hours</option>
                  <option value={24}>Expires in 24 hours</option>
                  <option value={168}>Expires in 7 days</option>
                  <option value={720}>Expires in 30 days</option>
                </select>
                <button onClick={handleCreateLimitOrder} disabled={!amount || !limitPrice}
                  className="w-full py-3 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 hover:bg-amber-400 disabled:opacity-40 flex items-center justify-center gap-2"><Target className="w-4 h-4" /> Create Limit Order</button>
              </div>
              {limitOrders.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-gray-500 text-xs font-medium">Orders ({limitOrders.length})</p>
                  {limitOrders.map(o => {
                    const isReady = o.status === 'active' && o.triggerStatus === 'ready_to_execute';
                    const statusColor =
                      o.status === 'filled' ? 'text-emerald-400' :
                      o.status === 'cancelled' || o.status === 'expired' ? 'text-gray-500' :
                      isReady ? 'text-amber-400' :
                      'text-cyan-400';
                    const statusLabel =
                      o.status === 'filled' ? 'FILLED' :
                      o.status === 'cancelled' ? 'CANCELLED' :
                      o.status === 'expired' ? 'EXPIRED' :
                      isReady ? 'READY ⚡' :
                      'MONITORING';
                    return (
                      <div key={o.orderId} className={`bg-dark-700/30 rounded-lg p-2.5 border ${isReady ? 'border-amber-400/40' : 'border-dark-600/30'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-xs">{o.fromAmount} {o.fromSymbol} → {o.toSymbol}</span>
                            <span className={`${statusColor} text-[9px] font-bold`}>{statusLabel}</span>
                          </div>
                          {o.status === 'active' && (
                            <div className="flex items-center gap-2">
                              {isReady && (
                                <button onClick={() => handleExecuteLimitOrder(o)}
                                  className="text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 px-2 py-1 rounded-lg font-bold">
                                  Execute Now
                                </button>
                              )}
                              <button onClick={() => { cancelLimitOrder(o.orderId); loadLimitOrders(); }} className="text-red-400 text-[10px] hover:text-red-300">Cancel</button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-gray-500">
                          <span>Target: {o.direction === 'above' ? '≥' : '≤'} {o.targetPrice} {o.toSymbol}/{o.fromSymbol}</span>
                          {o.lastSeenPrice && <span>Last: {Number(o.lastSeenPrice).toFixed(6)}</span>}
                        </div>
                        {o.txHash && (
                          <a href={`https://${o.chain === 'polygon' ? 'polygonscan.com' : o.chain === 'arbitrum' ? 'arbiscan.io' : o.chain === 'base' ? 'basescan.org' : 'etherscan.io'}/tx/${o.txHash}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-[9px] font-mono text-gray-500 hover:text-amber-400 mt-1 inline-flex items-center gap-1">
                            {o.txHash.slice(0, 10)}…{o.txHash.slice(-8)} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── DCA Tab ── */}
          {activeTab === 'dca' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Repeat className="w-4 h-4 text-blue-400" /> DCA Scheduler</h3>
              <p className="text-gray-500 text-xs mb-4">Automatically buy tokens on a schedule — dollar-cost averaging with best-route execution.</p>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button onClick={() => setShowFromPicker(true)} className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-sm text-white text-left">{fromToken?.symbol || 'Spend'}</button>
                  <ArrowRight className="w-4 h-4 text-gray-600 self-center" />
                  <button onClick={() => setShowToPicker(true)} className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-sm text-white text-left">{toToken?.symbol || 'Buy'}</button>
                </div>
                <input type="number" value={dcaAmount} onChange={e => setDcaAmount(e.target.value)} placeholder="Amount per swap" className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
                <div className="grid grid-cols-3 gap-2">
                  {['hourly', 'daily', 'weekly'].map(f => (
                    <button key={f} onClick={() => setDcaFrequency(f)} className={`py-2 rounded-lg text-xs font-medium capitalize ${dcaFrequency === f ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'bg-dark-700 text-gray-500 border border-dark-600'}`}>{f}</button>
                  ))}
                </div>
                <input type="number" value={dcaTotalSwaps} onChange={e => setDcaTotalSwaps(e.target.value)} placeholder="Total swaps" className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
                {dcaAmount && dcaTotalSwaps && (
                  <div className="bg-dark-700/30 rounded-xl p-3 border border-dark-600/30 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-gray-500">Total investment</span><span className="text-white font-mono">{parseFloat(dcaAmount) * parseInt(dcaTotalSwaps)} {fromToken?.symbol}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Duration</span><span className="text-white">{dcaTotalSwaps} {dcaFrequency} swaps</span></div>
                  </div>
                )}
                <button onClick={handleCreateDca} disabled={!dcaAmount || !dcaTotalSwaps}
                  className="w-full py-3 rounded-xl text-sm font-bold bg-blue-500 text-white hover:bg-blue-400 disabled:opacity-40 flex items-center justify-center gap-2"><Repeat className="w-4 h-4" /> Start DCA</button>
              </div>
              {dcaSchedules.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-gray-500 text-xs font-medium">Active DCA ({dcaSchedules.length})</p>
                  {dcaSchedules.map(s => (
                    <div key={s.dcaId} className="flex items-center justify-between bg-dark-700/30 rounded-lg p-2.5 border border-dark-600/30">
                      <div><p className="text-white text-xs">{s.amountPerSwap} {s.fromSymbol} → {s.toSymbol} ({s.frequency})</p><p className="text-gray-500 text-[10px]">{s.completedSwaps}/{s.totalSwaps} complete</p></div>
                      <button onClick={() => { cancelDcaSchedule(s.dcaId); loadDcaSchedules(); }} className="text-red-400 text-[10px] hover:text-red-300">Cancel</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Discover Tab (left side — trending) ── */}
          {activeTab === 'discover' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" /> Trending on CoinGecko
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 ml-auto">Live</span>
              </h3>
              {trendingTokens.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">Loading trending tokens…</p>
              ) : (
                <div className="space-y-2">
                  {trendingTokens.map((t, i) => (
                    <a
                      key={t.coinId || t.symbol || i}
                      href={t.coinId ? `/coin/${t.coinId}` : '#'}
                      className="flex items-center gap-3 bg-dark-700/30 rounded-xl p-3 border border-dark-600/30 hover:border-dark-500 transition-all"
                    >
                      <span className="text-gray-600 text-xs w-5">{t.rank ?? i + 1}</span>
                      {t.thumb ? (
                        <img src={t.thumb} alt={t.symbol} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-dark-600 flex items-center justify-center text-xs font-bold text-white">{t.symbol?.slice(0, 2)}</div>
                      )}
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{t.name || t.symbol}</p>
                        <p className="text-gray-600 text-[10px] font-mono">{t.symbol}{t.marketCapRank ? ` · #${t.marketCapRank} mcap` : ''}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-600" />
                    </a>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3 text-[10px] text-gray-600 flex-wrap">
                <span>Live data from CoinGecko</span>
                {trendingFetchedAt && (
                  <>
                    <span>·</span>
                    <TimeAgo timestamp={trendingFetchedAt} prefix="updated" />
                  </>
                )}
                <span>·</span>
                <span>click any token to see full details</span>
              </div>
            </div>
          )}

          {/* ── History Tab (left side) ── */}
          {activeTab === 'history' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" /> Swap History</h3>
              {swapHistory.length === 0 ? (
                <div className="text-center py-8"><p className="text-gray-500 text-sm">No swaps yet</p><p className="text-gray-600 text-xs mt-1">Your swap history will appear here</p></div>
              ) : (
                <div className="space-y-2">
                  {swapHistory.map(s => {
                    // The backend record uses `submittedAt` (when frontend posted
                    // the txHash) and `confirmedAt` (when pendingChecker saw the
                    // on-chain receipt). Previously this rendered `s.timestamp`
                    // which doesn't exist → "Invalid Date" everywhere.
                    const ts = s.confirmedAt || s.submittedAt;
                    const tsValid = ts && !isNaN(new Date(ts).getTime());
                    const statusColor =
                      s.status === 'confirmed' ? 'text-emerald-400' :
                      s.status === 'failed'    ? 'text-red-400'     :
                      s.status === 'pending'   ? 'text-amber-400'   :
                      'text-gray-400';
                    return (
                      <div key={s.swapId} className="bg-dark-700/30 rounded-xl p-3 border border-dark-600/30">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-xs font-medium">{s.fromSymbol} → {s.toSymbol}</span>
                          <span className={`${statusColor} text-[10px] uppercase font-bold`}>{s.status || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 text-[10px]">
                            {s.amountIn} {s.fromSymbol || ''} →{' '}
                            {s.amountOut != null
                              ? `${fmtAmt(s.amountOut, 6)} ${s.toSymbol || ''}`
                              : s.status === 'confirmed'
                                ? `filled ${s.toSymbol || ''} (check wallet)`
                                : s.status === 'failed'
                                  ? 'tx reverted'
                                  : s.status === 'abandoned'
                                    ? 'not found on-chain'
                                    : 'pending fill'}
                          </span>
                          <span className="text-gray-600 text-[10px]">
                            {tsValid ? new Date(ts).toLocaleString() : '—'}
                          </span>
                        </div>
                        {s.txHash && (
                          <a
                            href={`https://${s.chain === 'polygon' ? 'polygonscan.com' : s.chain === 'arbitrum' ? 'arbiscan.io' : s.chain === 'base' ? 'basescan.org' : s.chain === 'bsc' ? 'bscscan.com' : s.chain === 'avalanche' ? 'snowtrace.io' : s.chain === 'optimism' ? 'optimistic.etherscan.io' : 'etherscan.io'}/tx/${s.txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[9px] font-mono text-gray-500 hover:text-amber-400 mt-1 inline-flex items-center gap-1"
                          >
                            {s.txHash.slice(0, 10)}…{s.txHash.slice(-8)} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </>

        {/* Pre-swap checks and Route Comparison render below the swap card in
            the same centered column. They were previously in a separate
            col-span-2 right column that looked empty without a quote. */}
        <>

          {/* Pre-swap insights — rule-based pre-trade checks (not AI) */}
          {swapInsights && amount && parseFloat(amount) > 0 && activeTab === 'swap' && (
            <div className={`bg-dark-800 rounded-2xl border p-5 ${swapInsights.riskScore > 6 ? 'border-red-500/20' : swapInsights.riskScore > 3 ? 'border-amber-500/20' : 'border-emerald-500/20'}`}>
              <h3 className="text-white font-bold mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2"><Info className="w-4 h-4 text-blue-400" /> Pre-swap checks</span>
                <span className={`text-sm font-bold ${swapInsights.riskScore > 6 ? 'text-red-400' : swapInsights.riskScore > 3 ? 'text-amber-400' : 'text-emerald-400'}`}>Risk: {swapInsights.riskScore}/10</span>
              </h3>
              <div className="space-y-2">
                {swapInsights.insights.map((insight, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {insight.type === 'critical' && <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'warn' && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'good' && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'info' && <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />}
                    <p className={`text-xs ${insight.type === 'critical' ? 'text-red-300' : insight.type === 'warn' ? 'text-amber-300' : 'text-gray-400'}`}>{insight.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Route visualization */}
          {quote && activeTab === 'swap' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-amber-400" /> Route Comparison (3 Aggregators)</h3>
              <RouteVisualizer routes={quote.allRoutes} />
            </div>
          )}

          {/* Marketing tiles ("DEX Trading Suite" + "7 Chains Supported") removed.
              They duplicated the chain selector at the top and listed features
              (Sandwich Detector, AI Swap Advisor, MEV Protection-on-every-swap)
              that aren't truthfully available on manual user-wallet swaps. */}
        </>
      </div>

      {/* Token Pickers */}
      {showFromPicker && <TokenPicker chain={chain} selected={fromToken} onSelect={t => { setFromToken(t); setQuote(null); }} onClose={() => setShowFromPicker(false)} label="Select token to pay" />}
      {showToPicker && <TokenPicker chain={chain} selected={toToken} onSelect={t => { setToToken(t); setQuote(null); }} onClose={() => setShowToPicker(false)} label="Select token to receive" />}
    </div>
  );
}
